/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { faSave } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Formik, type FormikHelpers, type FormikProps } from 'formik'
import { isEqual } from 'lodash'
import { useMemo, type FC } from 'react'
import { Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap'
import * as yup from 'yup'

export const validationSchema = yup.object().shape({
  description: yup.string(),
  name: yup.string(),
  value: yup.mixed()
    .nullable()
    .transform(value => {
      try {
        return JSON.parse(value)
      } catch {
        return value
      }
    })
    .test("json", "Invalid JSON format", (value) => {
      try {
        if (typeof value === 'string') {
          JSON.parse(value)
        }

        if (typeof value === 'object') {
          JSON.stringify(value)
        }
        return true
      } catch {
        return false
      }
    })
    .default('{}'),
  status: yup.string()
    .oneOf(['active', 'inactive'], 'A situação deve ser ativo ou inativo')
    .default('inactive'),
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormFeatureFlagHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormFeatureFlagHandleSubmit,
  initialValues?: Partial<ValidationSchema>
}

const FormFeatureFlag: FC<FormikProps<ValidationSchema>> = ({
  handleSubmit,
  handleChange,
  handleBlur,
  isSubmitting,
  isValid,
  values,
  errors,
  initialValues = {},
}) => {
  const isChanged: boolean = useMemo(() => !isEqual(values, initialValues), [values, initialValues])

  const parseValue = (value: any) => {
    if (value === null || value === undefined) {
      return '{}'
    }

    if (typeof values.value === 'string') {
      return values.value
    }

    return JSON.stringify(values.value, null, 2)
  }

  return (
    <Row>
      <Col md={12}>
        <form onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} md={12} className="mb-3">
              <FloatingLabel label="Nome da feature flag (identificador)">
                <Form.Control
                  type='text'
                  name='name'
                  value={values.name ?? ''}
                  placeholder='Nome da feature flag'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
              {errors.name && <p>{errors.name}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <FloatingLabel label="Descrição">
                <Form.Control
                  type='text'
                  name='description'
                  value={values.description ?? ''}
                  placeholder='Descrição da feature flag'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
              {errors.description && <p>{errors.description}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className='mb-3'>
              <FloatingLabel label="Ativo">
                <Form.Select
                  value={values?.status ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  name="status"
                >
                  <option>Selecione uma opção</option>
                  <option value='active'>Sim</option>
                  <option value='inactive'>Não</option>
                </Form.Select>
              </FloatingLabel>
              {errors.status && <p>{errors.status}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <FloatingLabel label="Payload associado à feature flag (JSON)">
                <Form.Control
                  as="textarea"
                  rows={5}
                  style={{ height: '150px' }}
                  name='value'
                  disabled={isSubmitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Metadados associados ao evento"
                  value={parseValue(values.value)}
                />
              </FloatingLabel>
              {errors.value && <p>{errors.value}</p>}
            </Form.Group>
            <Col md={12}>
              <Button type="submit" size='sm' variant='primary' disabled={!isValid || isSubmitting || !isChanged}>
                <FontAwesomeIcon icon={faSave} />
                {' '}
                Salvar
              </Button>
            </Col>
          </Row>
        </form>
      </Col>
      {process.env.NODE_ENV === 'development' && (
        <Col md={12} className='mt-3'>
          <pre>{JSON.stringify(values, undefined, 2)}</pre>
        </Col>
      )}
    </Row>
  )
}

const FormFeatureFlagWrapper: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {}
}) => (
  <Formik
    component={FormFeatureFlag}
    validateOnChange
    validateOnBlur
    validateOnMount
    enableReinitialize
    validationSchema={validationSchema}
    initialValues={validationSchema.cast(initialValues)}
    onSubmit={onSubmit}
  />
)

export default FormFeatureFlagWrapper
